@let loaded = currentSelectors.finishedLoading | select:false;
@let isMdOrAbove = viewportSelectors.isMdAndUp | select;
@let isSm = viewportSelectors.isSm | select;
@let itemRequest = currentSelectors.itemRequest | select;
@let cancelable = currentSelectors.cancelable | select;
@let mxDetailFieldDefaults = {
  titleCSSClasses: 'text-title-large',
  valueCSSClasses: 'text-body-large text-on-surface-variant',
  truncateValue: true,
  truncateTitleTextMaxLines: 3,
  truncateTitleText: false
};

<div mxHiddenUntilLoaded [reveal]="loaded" class="flex flex-col px-4 md:px-6 lg:px-10 py-7 gap-7">
  <!-- Page Header -->
  <div class="flex flex-row justify-between justify-items-start">
    <div class="flex flex-row gap-2">
      <mx-icon-button icon="arrow_back" routeName="ITEM_REQUESTS" ariaLabel="Go Back"/>

      <div class="flex flex-col gap-2">
        <p class="text-headline-large">Item Request Details</p>
        <mat-chip-set>
          <mx-status-chip [status]="currentSelectors.status | select" [colors]="chipColors" [displayableStatusNames]="chipNames"/>
        </mat-chip-set>
      </div>
    </div>

    @if (isMdOrAbove) {
      <ng-container *ngTemplateOutlet="cancelRequestButton"/>
    }
  </div>

  <!-- Page Details -->
  <div class="flex flex-col md:flex-row gap-6 lg:gap-10 @container/page">
    <!-- Linked Catalog Record (if exists) and Submission Details -->
    <div class="flex flex-col w-full md:w-1/2 gap-6 lg:gap-10">
      <!-- Linked Catalog Record -->
      @if (currentSelectors.hasLinkedCatalogRecord | select) {
        @if (isMdOrAbove) {
          <mat-card class="bg-ref-neutral-variant-95">
            <mat-card-title class="text-headline-small pb-0">Catalog Record</mat-card-title>
            <mat-card-content class="flex flex-row gap-4 px-4 py-4">
              <ng-container *ngTemplateOutlet="catalogRecordContent"/>
            </mat-card-content>
          </mat-card>
        } @else {
          <mx-expandable-card [lazyLoadContent]="false" [expansionState]="catalogRecordExpansionState"
                              expansionPanelClasses="bg-ref-neutral-variant-95"
                              (expansionStateChanged)="catalogRecordExpansionState = $event">
            <div header class="w-full">
              <div class="flex flex-row justify-between items-center w-full py-3 text-headline-small">
                <p>Catalog Record</p>
                <mx-expandable-card-toggle-button [state]="catalogRecordExpansionState" [collapsedRotation]="-90" [expandedRotation]="0"/>
              </div>
            </div>

            <ng-container *ngTemplateOutlet="catalogRecordContent"/>
          </mx-expandable-card>
        }
      }

      <!-- Item Request Submission Details -->
      @if (isMdOrAbove) {
        <mat-card class="bg-surface2">
          <mat-card-title class="text-headline-small pb-0">Submission Details</mat-card-title>
          <mat-card-content class="px-5 py-4 lg:px-10">
            <ng-container *ngTemplateOutlet="itemRequestContent"/>
          </mat-card-content>
        </mat-card>
      } @else {
        <mx-expandable-card [lazyLoadContent]="false" [expansionState]="itemRequestExpansionState" expansionPanelClasses="bg-ref-neutral-variant-95"
                            (expansionStateChanged)="itemRequestExpansionState = $event" class="mx-elevation-0">
          <div header class="w-full">
            <div class="flex flex-row justify-between items-center w-full py-3 text-headline-small">
              <p>Submission Details</p>
              <mx-expandable-card-toggle-button [state]="itemRequestExpansionState" [collapsedRotation]="-90" [expandedRotation]="0"/>
            </div>
          </div>

          <ng-container *ngTemplateOutlet="itemRequestContent"/>
        </mx-expandable-card>
      }
    </div>

    <!-- Messages -->
    @if (isMdOrAbove) {
      <mat-card class="w-full md:w-1/2">
        <mat-card-title class="text-headline-small pb-0">Messages</mat-card-title>
        <mat-card-content>
          <ng-container *ngTemplateOutlet="messagesContent"/>
        </mat-card-content>
      </mat-card>
    } @else {
      <mx-expandable-card [lazyLoadContent]="false" [expansionState]="messagesExpansionState" (expansionStateChanged)="messagesExpansionState = $event"
                          class="mx-elevation-0">
        <div header class="w-full">
          <div class="flex flex-row justify-between items-center w-full py-3 text-headline-small">
            <p>Messages</p>
            <mx-expandable-card-toggle-button [state]="messagesExpansionState" [collapsedRotation]="-90" [expandedRotation]="0"/>
          </div>
        </div>

        <ng-container *ngTemplateOutlet="messagesContent"/>
      </mx-expandable-card>
    }

    @if (isSm) {
      <div class="flex flex-row w-full justify-center">
        <ng-container *ngTemplateOutlet="cancelRequestButton"/>
      </div>
    }
  </div>

  <!-- Cancel Request Button -->
  <ng-template #cancelRequestButton>
    <mx-filled-button label="Cancel Request" [enabled]="!!cancelable" disabledTooltip="This request is already settled and cannot be canceled."
                      (click)="onClickCancel(itemRequest)"/>
  </ng-template>

  <!-- Catalog Record Content -->
  <ng-template #catalogRecordContent>
    <div>
      <mx-cover-image [catalogRecord]="currentSelectors.linkedCatalogRecord | select" [widthRem]="11" [heightRem]="13.5" [priority]="true" xAlignment="LEFT"/>
    </div>
    <div [mxDetailFieldDefaults]="mxDetailFieldDefaults" class="flex-col gap-4">
      <mx-detail-field titleText="Title:" [value]="currentSelectors.linkedCatalogRecordTitle | select"/>
      <mx-detail-field titleText="Author:" [value]="currentSelectors.linkedCatalogRecordAuthor | select"/>
      <mx-detail-field titleText="Available:" [value]="currentSelectors.availabilityAvailableCount | select"/>
      <mx-detail-field titleText="Holds:" [value]="currentSelectors.availabilityOnHoldCount | select"/>
    </div>
  </ng-template>

  <!-- Item Request Content -->
  <ng-template #itemRequestContent>
    <div [mxDetailFieldDefaults]="mxDetailFieldDefaults" class="flex-col gap-4">
      <mx-detail-field titleText="Date Requested:" [value]="currentSelectors.created | select | format:'DATE_ONLY'"/>
      <mx-detail-field titleText="Title:" [value]="currentSelectors.title | select"/>
      <mx-detail-field titleText="Author:" [value]="currentSelectors.author | select"/>
      <mx-detail-field titleText="ISBN:" [value]="currentSelectors.isbn | select"/>
      <mx-detail-field titleText="Material Type:" [value]="currentSelectors.materialTypeDesc | select"/>
      <mx-detail-field titleText="Pickup Location:" [value]="currentSelectors.pickupBranchName | select"/>
      <mx-detail-field titleText="Year of Publication:" [value]="currentSelectors.yearOfPublication | select"/>
      <mx-detail-field titleText="Print Type:" [value]="currentSelectors.printType | select"/>
      <mx-detail-field titleText="Note:" [value]="currentSelectors.notes | select"/>
    </div>
  </ng-template>

  <!-- Messages Content -->
  <ng-template #messagesContent>
    <px-item-request-details-messages [itemRequest]="currentSelectors.itemRequest | select:emptyRequest"/>
  </ng-template>
</div>
